import React from 'react';
import { Heart, CheckCircle, TrendingUp, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';

const Cardio = () => {
  const navigate = useNavigate();

  const milestones = [
    { period: 'Q4 2025 - Q2 2026', title: 'Prototype Development', status: 'In Progress' },
    { period: 'Q3 2026 - Q2 2027', title: 'Clinical Validation', status: 'Planned' },
    { period: 'Q3 2027 - Q4 2028', title: 'MDR/CE Certification', status: 'Planned' },
    { period: '2029 - 2030', title: 'Market Introduction', status: 'Planned' },
  ];

  const features = [
    'YOLOv8-based echocardiography segmentation',
    'Real-time ECG pattern analysis',
    'SWMA (Segmental Wall Motion Abnormality) detection',
    'Explainable AI visualization with attention maps',
    'Multi-modal data fusion (Echo + ECG)',
    'Clinical decision support dashboard',
  ];

  const impact = [
    { metric: '95%+', label: 'Target Accuracy', icon: Award },
    { metric: '40%', label: 'Reduced Diagnostic Time', icon: TrendingUp },
    { metric: '30%', label: 'Bias Reduction', icon: Users },
    { metric: '100%', label: 'Explainability', icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220]">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#14B8A6]/20 via-[#0B1220] to-[#7C3AED]/20 border-b border-[#1F2A3A] pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1715111965684-e2cdde4a1f5c" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-[#14B8A6]/20 flex items-center justify-center">
              <Heart size={32} className="text-[#14B8A6]" />
            </div>
            <div>
              <Badge className="bg-[#14B8A6]/20 text-[#14B8A6] border-[#14B8A6]/30 mb-2">
                In R&D
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                Yamai Cardio
              </h1>
            </div>
          </div>
          <p className="text-[#8A90A0] text-xl max-w-3xl">
            Early AI Detection of Cardiac Abnormalities Through Explainable Multi-Modal Analysis
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Overview */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Overview</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <p className="text-[#E6E8EF] text-lg leading-relaxed mb-4">
              Yamai Cardio is our flagship AI system designed to revolutionize cardiac diagnostics through
              the intelligent fusion of echocardiography and electrocardiogram (ECG) data. By leveraging
              explainable AI techniques, we empower cardiologists to detect segmental wall motion abnormalities
              (SWMA) earlier and with greater confidence.
            </p>
            <p className="text-[#8A90A0] leading-relaxed">
              Unlike traditional black-box AI systems, Yamai Cardio provides transparent insights into its
              decision-making process, showing clinicians exactly which cardiac regions and ECG patterns
              influenced the diagnosis.
            </p>
          </div>
        </section>

        {/* The Problem */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">The Problem</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
              <h3 className="text-xl font-semibold text-white mb-3">Delayed Detection</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                SWMA often goes undetected in early stages, leading to delayed intervention and increased
                risk of heart failure and cardiac events.
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
              <h3 className="text-xl font-semibold text-white mb-3">Interpretation Variability</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Manual echocardiography interpretation is subjective and time-consuming, with significant
                inter-observer variability affecting diagnostic consistency.
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
              <h3 className="text-xl font-semibold text-white mb-3">Limited Integration</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Echo and ECG data are typically analyzed separately, missing valuable correlations that
                could improve diagnostic accuracy.
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
              <h3 className="text-xl font-semibold text-white mb-3">Black Box AI</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Existing AI solutions lack transparency, making clinicians hesitant to trust automated
                recommendations without understanding the reasoning.
              </p>
            </div>
          </div>
        </section>

        {/* Our Solution */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Our Solution</h2>
          <div className="bg-gradient-to-br from-[#14B8A6]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#14B8A6]/30">
            <p className="text-[#E6E8EF] text-lg leading-relaxed mb-6">
              Yamai Cardio combines state-of-the-art computer vision and signal processing to create a
              comprehensive cardiac diagnostic assistant:
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle size={24} className="text-[#14B8A6]" />
                  Echocardiography Analysis
                </h3>
                <ul className="space-y-2 text-[#8A90A0]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#14B8A6] mt-1">•</span>
                    <span>YOLOv8-based cardiac chamber segmentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#14B8A6] mt-1">•</span>
                    <span>Automated wall motion tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#14B8A6] mt-1">•</span>
                    <span>17-segment LV model analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#14B8A6] mt-1">•</span>
                    <span>Attention-based explainability overlays</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle size={24} className="text-[#7C3AED]" />
                  ECG Integration
                </h3>
                <ul className="space-y-2 text-[#8A90A0]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#7C3AED] mt-1">•</span>
                    <span>Real-time 12-lead ECG analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7C3AED] mt-1">•</span>
                    <span>ST-segment and T-wave pattern detection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7C3AED] mt-1">•</span>
                    <span>Multi-modal data fusion layer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7C3AED] mt-1">•</span>
                    <span>Temporal correlation analysis</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Stack */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Technology Stack</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Computer Vision</h3>
                <ul className="space-y-2 text-[#8A90A0] text-sm">
                  <li>YOLOv8 (object detection)</li>
                  <li>U-Net (segmentation)</li>
                  <li>ResNet-50 (feature extraction)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Signal Processing</h3>
                <ul className="space-y-2 text-[#8A90A0] text-sm">
                  <li>1D CNN for ECG analysis</li>
                  <li>LSTM for temporal patterns</li>
                  <li>Wavelet transforms</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Explainability</h3>
                <ul className="space-y-2 text-[#8A90A0] text-sm">
                  <li>Grad-CAM attention maps</li>
                  <li>SHAP value analysis</li>
                  <li>Feature importance ranking</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-[#1F2A3A]">
              <h3 className="text-lg font-semibold text-white mb-3">Frameworks & Tools</h3>
              <div className="flex flex-wrap gap-2">
                {['PyTorch', 'TensorFlow', 'OpenCV', 'SciPy', 'Pandas', 'FastAPI'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-[#7C3AED]/20 text-[#7C3AED] text-sm rounded-lg border border-[#7C3AED]/30"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-3 bg-[#1F2A3A]/30 backdrop-blur-sm rounded-lg p-4 border border-[#1F2A3A]"
              >
                <CheckCircle size={20} className="text-[#14B8A6] flex-shrink-0" />
                <span className="text-[#E6E8EF]">{feature}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Expected Impact */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Expected Impact</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {impact.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#14B8A6]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-xl p-6 border border-[#14B8A6]/30 text-center"
              >
                <item.icon size={32} className="text-[#14B8A6] mx-auto mb-3" />
                <div className="text-4xl font-bold text-white mb-2">{item.metric}</div>
                <div className="text-[#8A90A0] text-sm">{item.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Validation Plan */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Clinical Validation Plan</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <p className="text-[#E6E8EF] mb-6 leading-relaxed">
              Yamai Cardio will undergo rigorous clinical validation in partnership with leading hospitals
              and research institutions in the Netherlands and Belgium:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#7C3AED] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Retrospective Study (Q3-Q4 2026)</h3>
                  <p className="text-[#8A90A0] text-sm">Validation on 5,000+ historical echocardiography + ECG cases</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#7C3AED] font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Prospective Trial (Q1-Q2 2027)</h3>
                  <p className="text-[#8A90A0] text-sm">Real-time validation with 1,000+ new patients across 3 hospitals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#7C3AED] font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Peer Review & Publication (Q3 2027)</h3>
                  <p className="text-[#8A90A0] text-sm">Results submission to leading cardiology journals</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap/Milestones */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Development Roadmap</h2>
          <div className="space-y-4">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A] flex items-center justify-between"
              >
                <div>
                  <p className="text-[#7C3AED] font-mono text-sm mb-1">{milestone.period}</p>
                  <h3 className="text-xl font-semibold text-white">{milestone.title}</h3>
                </div>
                <Badge
                  className={`${
                    milestone.status === 'In Progress'
                      ? 'bg-[#14B8A6]/20 text-[#14B8A6] border-[#14B8A6]/30'
                      : 'bg-[#8A90A0]/20 text-[#8A90A0] border-[#8A90A0]/30'
                  }`}
                >
                  {milestone.status}
                </Badge>
              </div>
            ))}
          </div>
        </section>

        {/* Related Projects */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Related Projects</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Yamai Neuro', path: '/projects/neuro', color: '#7C3AED', desc: 'Brain & neural analysis' },
              { name: 'Yamai Vision', path: '/projects/vision', color: '#8B5CF6', desc: 'Retinal diagnostics' },
              { name: 'Yamai HealthNet', path: '/projects/healthnet', color: '#F59E0B', desc: 'Unified platform' },
            ].map((project, index) => (
              <div
                key={index}
                onClick={() => navigate(project.path)}
                className="cursor-pointer bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A] hover:border-[#14B8A6]/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <h3 className="text-xl font-semibold text-white mb-2">{project.name}</h3>
                <p className="text-[#8A90A0] text-sm">{project.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-[#7C3AED]/20 via-[#0B1220] to-[#14B8A6]/20 rounded-2xl p-12 border border-[#7C3AED]/30">
            <h2 className="text-3xl font-bold text-white mb-4">Interested in Collaboration?</h2>
            <p className="text-[#8A90A0] mb-8 max-w-2xl mx-auto">
              We're seeking clinical partners and research institutions for validation studies.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] hover:opacity-90 text-white px-8 py-6 rounded-xl"
            >
              Contact Us
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Cardio;